import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en/translation.json';

/* ── Lazy locales (2026-08-29 performance fix) ──────────────────────────────
   All 8 translation files used to be imported eagerly, putting ~367 KB of
   JSON into main.js — every visitor downloaded every language to use one.
   Now only English (the fallback) ships in the main bundle; the detected /
   selected language is fetched as its own tiny chunk. To keep the first
   paint from flashing English at non-English users, the ACTIVE language's
   bundle is loaded BEFORE i18next init when we can see it synchronously
   (localStorage / navigator) — init just awaits that one import.          */
const LOCALE_LOADERS = {
  ar: () => import(/* webpackChunkName: "locale-ar" */ './locales/ar/translation.json'),
  fr: () => import(/* webpackChunkName: "locale-fr" */ './locales/fr/translation.json'),
  es: () => import(/* webpackChunkName: "locale-es" */ './locales/es/translation.json'),
  tr: () => import(/* webpackChunkName: "locale-tr" */ './locales/tr/translation.json'),
  ur: () => import(/* webpackChunkName: "locale-ur" */ './locales/ur/translation.json'),
  ru: () => import(/* webpackChunkName: "locale-ru" */ './locales/ru/translation.json'),
  de: () => import(/* webpackChunkName: "locale-de" */ './locales/de/translation.json'),
};
const loadedLocales = new Set(['en']);

const ensureLocale = async (lng) => {
  const code = String(lng || '').split('-')[0];
  if (!code || loadedLocales.has(code) || !LOCALE_LOADERS[code]) return;
  try {
    const mod = await LOCALE_LOADERS[code]();
    // Guard again — a concurrent load may have won the race.
    if (!loadedLocales.has(code)) {
      i18n.addResourceBundle(code, 'translation', mod.default || mod, true, true);
      loadedLocales.add(code);
      // Re-render everything that already asked for this language and got
      // the English fallback while the chunk was in flight.
      if ((i18n.language || '').split('-')[0] === code) i18n.emit('languageChanged', i18n.language);
    }
  } catch {
    /* offline / blocked chunk → English fallback keeps the site usable */
  }
};

// Languages metadata for the selector
export const LANGUAGES = [
  { code: 'en', label: 'English',  flag: 'gb', dir: 'ltr' },
  { code: 'ar', label: 'العربية',  flag: 'sa', dir: 'rtl' },
  { code: 'fr', label: 'Français', flag: 'fr', dir: 'ltr' },
  { code: 'es', label: 'Español',  flag: 'es', dir: 'ltr' },
  { code: 'tr', label: 'Türkçe',   flag: 'tr', dir: 'ltr' },
  { code: 'ur', label: 'اردو',     flag: 'pk', dir: 'rtl' },
  { code: 'ru', label: 'Русский',   flag: 'ru', dir: 'ltr' },
  { code: 'de', label: 'Deutsch',   flag: 'de', dir: 'ltr' },
];
const SUPPORTED = LANGUAGES.map(l => l.code);

// Mirror the detector's own order (localStorage → navigator) synchronously so
// the active language's chunk starts downloading immediately, in parallel
// with everything else the page is loading.
const detectInitialLng = () => {
  try {
    const stored = localStorage.getItem('hm_language');
    if (stored) return stored.split('-')[0];
  } catch { /* private mode */ }
  const nav = (navigator.languages && navigator.languages[0]) || navigator.language || 'en';
  return String(nav).split('-')[0];
};
const initialLng = detectInitialLng();
const initialLocaleReady = ensureLocale(initialLng);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'hm_language',
      caches: ['localStorage'],
    },
    // partialBundledLanguages: resources holds only en at init; the active
    // language is merged in via addResourceBundle. Without this flag i18next
    // would treat every other language as unsupported-and-missing.
    partialBundledLanguages: true,
  });

// Any later language switch (selector, admin) fetches its bundle on demand.
i18n.on('languageChanged', (lng) => { ensureLocale(lng); });

// Set document direction on language change
const setDir = (lng) => {
  const lang = LANGUAGES.find(l => l.code === String(lng || '').split('-')[0]);
  document.documentElement.dir = lang?.dir || 'ltr';
  document.documentElement.lang = lng;
};
setDir(i18n.language);
i18n.on('languageChanged', setDir);

// Tell the server which language to preload on the NEXT page load (it adds a
// <link rel=preload> for that locale chunk to the HTML — see server.js
// withLocalePreload). Without this it can only guess from Accept-Language.
const rememberLangForServer = (lng) => {
  try {
    const code = String(lng || '').split('-')[0];
    if (/^[a-z]{2}$/.test(code)) document.cookie = `hm_lang=${code}; path=/; max-age=31536000; samesite=lax`;
  } catch { /* cookies disabled */ }
};
rememberLangForServer(i18n.language);
i18n.on('languageChanged', rememberLangForServer);

// Lets index.js delay the first render a beat until the visitor's own
// language is in memory (bounded — see index.js), so non-English visitors
// never see an English flash.
export const i18nReady = initialLocaleReady;

// NOTE: the site previously embedded the Google Translate widget alongside
// i18next, which machine-translated the ALREADY-TRANSLATED page (turning
// correct Arabic into gibberish) and needed a site-wide MutationObserver
// stamping `notranslate` on every mounted button/label to contain it. The
// widget and that observer are gone — i18next owns all translation. The
// `notranslate`/translate="no" attributes still sprinkled on proper nouns
// (hotel names, brand, emails) remain useful: they stop the BROWSER's own
// translate feature from mangling names too.

export default i18n;

/**
 * Get the current i18n language for use in API calls.
 * Safe to call outside React components (in service classes, utils, etc.)
 */
export const getApiLanguage = () => i18n.language || localStorage.getItem('hm_language') || 'en';
